import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { PersonLink } from "@/components/common/PersonLink";
import { SearchableSelect } from "@/components/design/SearchableSelect";
import type { DashboardContext } from "./dashboard-kinds";
import { ProjectAnlassField } from "./ProjectAnlassField";
import { ANLASS_ACTIVITY } from "./project-mock-data";
import { PROJECT_STATUSES, PROJECT_STATUS_LABELS, parseStatus, parseTags, type ProjectStatus } from "./project-plan";
import { ProjectTags } from "./ProjectTags";

// Die Eigenschaften eines Projekts in der Übersicht: Status, Verantwortlich, Anlass und Tags.
// Alle vier liegen in `data` (Verantwortlich ist die Eigentümerin) und werden wie jede Änderung
// gebündelt gespeichert.
export function ProjectOverviewFields({ ctx }: { ctx: DashboardContext }) {
  const intl = useIntl();
  const status = parseStatus(ctx.data.status);
  const anlassId = typeof ctx.data.anlass === "string" ? ctx.data.anlass : null;
  const tags = parseTags(ctx.data.tags);
  const statusLabel = intl.formatMessage({ id: "project.meta.status", defaultMessage: "Status" });

  return (
    // Lesebreite statt voller Blattbreite: drei Angaben über 1500px zu ziehen macht aus einem
    // Datenblatt eine Landebahn. Ab @6xl steht rechts daneben eine schmale Spur für die Tags, statt
    // sie unter die Felder zu legen; darunter stapelt beides.
    <div className="grid max-w-5xl gap-x-8 gap-y-6 @6xl:grid-cols-[minmax(0,1fr)_16rem]">
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 @sm:grid-cols-2 @lg:grid-cols-3">
        <div className="min-w-0">
          <div className="smycles-field-label">{statusLabel}</div>
          <div className="mt-1">
            {/* Feste, kurze Liste: lesen geht schneller als tippen, deshalb ohne Suchzeile -
                dieselbe Entscheidung wie beim Anlass. */}
            <SearchableSelect
              id="project-status"
              value={status}
              onChange={(next) => ctx.editData({ status: next as ProjectStatus })}
              options={PROJECT_STATUSES.map((key) => ({
                value: key,
                label: intl.formatMessage(PROJECT_STATUS_LABELS[key]),
              }))}
              searchable={false}
              disabled={ctx.readOnly}
              placeholder={statusLabel}
              ariaLabel={statusLabel}
            />
          </div>
        </div>
        <div>
          <div className="smycles-field-label">
            <FormattedMessage id="project.meta.owner" defaultMessage="Verantwortlich" />
          </div>
          <div className="mt-1">
            {ctx.entity.company ? (
              <Link id="project-responsible-company" to={`/c/${ctx.entity.company.id}`} className="font-medium text-ink underline-offset-2 hover:underline">
                {ctx.entity.company.name}
              </Link>
            ) : ctx.entity.createdBy ? <PersonLink person={ctx.entity.createdBy} size="sm" /> : null}
          </div>
        </div>
        <div className="min-w-0">
          <div className="smycles-field-label">
            <FormattedMessage id="project.facet.anlass.label" defaultMessage="Anlass" />
          </div>
          <div className="mt-1">
            <ProjectAnlassField
              value={anlassId}
              onChange={(id) => {
                ctx.editData({ anlass: id });
                ctx.log("anlass", ANLASS_ACTIVITY);
              }}
              readOnly={ctx.readOnly}
            />
          </div>
        </div>
      </div>
      {/* Tags gehören zum Projekt, nicht zu einem Arbeitsschritt, also stehen sie bei den übrigen
          Eigenschaften, nur in der eigenen Spur, weil sie als einzige umbrechen dürfen. */}
      <div className="min-w-0">
        <div className="smycles-field-label">
          <FormattedMessage id="project.meta.tags" defaultMessage="Tags" />
        </div>
        <div className="mt-1.5">
          <ProjectTags tags={tags} onChange={(next) => ctx.editData({ tags: next })} readOnly={ctx.readOnly} />
        </div>
      </div>
    </div>
  );
}
